import axios, {AxiosInstance} from 'axios'
import MyAxiosInstanceDocument from './axiosInstanceDocument'
import {Question} from '../types/question'
import {project} from '../types/project'
import {Project} from '../types/getDocuments'

export class DocumentApi {
    private documentApi: AxiosInstance

    client = axios.create({
        baseURL: import.meta.env.VITE_DOCUMENT_URL,
    })

    constructor() {
        this.documentApi = MyAxiosInstanceDocument
    }

    async getResume(project_id: string): Promise<any> {
        try {
            const res = await this.documentApi.get(
                `/projects/${project_id}/answers`
            )
            return res.data
        } catch (error) {
            window.location.href = '/Erro'
            throw error
        }
    }

    async postAnswer(answer: any): Promise<Question> {
        try {
            const res = await this.documentApi.post(`/projects/answer`, answer)
            return res.data
        } catch (error) {
            window.location.href = '/Erro'
            console.log(error)
            throw error
        }
    }

    async getPreviousQuestion(
        project_id: string,
        question_id: string
    ): Promise<Question> {
        try {
            const res = await this.documentApi.get(
                `/projects/${project_id}/previous-question/${question_id}`
            )

            return res.data
        } catch (error: any) {
            window.location.href = '/Erro'
            throw error
        }
    }

    async getNextQuestion(project_id: string): Promise<Question> {
        try {
            const res = await this.documentApi.get(
                `/projects/${project_id}/next-question`
            )

            return res.data
        } catch (error: any) {
            window.location.href = '/Erro'
            throw error
        }
    }

    async finishProject(project_id: string): Promise<void> {
        try {
            await this.documentApi.post(`/projects/${project_id}/finish`)
            return
        } catch (error) {
            window.location.href = '/Erro'
            throw error
        }
    }

    async getProjects(limit = 15, skip = 0): Promise<project[]> {
        try {
            const res: {data: project[]} = await this.documentApi.get(
                `/projects?limit=${limit}&skip=${skip}`
            )
            return res.data
        } catch (error) {
            console.error(error)
            window.location.href = '/Erro'
            throw error
        }
    }

    async getProjectById(project_id: string): Promise<project> {
        try {
            const res = await this.documentApi.get(`/projects/${project_id}`)
            return res.data
        } catch (error) {
            console.error(error)
            throw new Error('Erro ao obter documentos da API.')
        }
    }

    async postProject(projectData: {
        name: string
        project_type_id: string
        document_types: string[]
    }): Promise<Question> {
        try {
            const response = await this.documentApi.post(
                '/projects',
                projectData
            )
            return response.data
        } catch (error) {
            console.error('Erro criando projeto:', error)
            if (axios.isAxiosError(error) && error.response) {
                window.location.href = '/Erro'
                throw new Error(
                    `Erro ao criar projeto: ${
                        error.response.data.message || error.response.statusText
                    }`
                )
            } else {
                window.location.href = '/Erro'
                throw new Error('Erro ao criar projeto: Erro desconhecido')
            }
        }
    }

    async archiveProject(project_guid: string): Promise<void> {
        try {
            await this.documentApi.delete(`/projects/${project_guid}/archive`)
            return
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    async unarchiveProject(project_guid: string): Promise<void> {
        try {
            await this.documentApi.put(`/projects/${project_guid}/restore`)
            return
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    async getArchivedProjects(): Promise<project[]> {
        try {
            const res = await this.documentApi.get('/projects/archived')
            return res.data
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    async getUserInfo(): Promise<any> {
        try {
            const res = await this.documentApi.get('/users')
            return res.data
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    async updateProject(projectData: {
        project_id: string
        document_types: string[]
    }) {
        try {
            const response = await this.documentApi.put(
                `/projects/${projectData.project_id}`,
                {
                    documents: projectData.document_types,
                }
            )
            return response.data
        } catch (error) {
            console.error('Erro atualizando projeto:', error)
            if (axios.isAxiosError(error) && error.response) {
                throw new Error(
                    `Erro ao atualizar projeto: ${
                        error.response.data.message || error.response.statusText
                    }`
                )
            } else {
                throw new Error('Erro ao atualizar projeto: Erro desconhecido')
            }
        }
    }

    async getQuestionById(
        project_id: string,
        question_id: string
    ): Promise<Question> {
        try {
            const res = await this.documentApi.get(
                `/projects/${project_id}/question/${question_id}`
            )
            return res.data
        } catch (error) {
            console.error('Erro ao obter pergunta:', error)
            throw error
        }
    }

    async downloadAllDocuments(project_id: string): Promise<void> {
        try {
            const response = await this.documentApi.get(
                `/projects/${project_id}/download-all`,
                {responseType: 'blob'}
            )

            const url = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', `project-${project_id}-documents.zip`)
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
        } catch (error) {
            console.error('Erro ao baixar documentos:', error)
            throw error
        }
    }

    async deleteDocument(
        project_id: string,
        document_id: string
    ): Promise<void> {
        try {
            await this.documentApi.delete(
                `/projects/${project_id}/document/${document_id}`
            )
        } catch (error) {
            console.error('Erro ao deletar documento:', error)
            throw error
        }
    }

    async getDocuments(project_id: string): Promise<Project> {
        try {
            const res = await this.documentApi.get(`/projects/${project_id}`)
            return res.data
        } catch (error) {
            console.error('Erro ao obter documentos:', error)
            throw error
        }
    }

    async getProjectQuestions(): Promise<any> {
        try {
            const res = await this.documentApi.get(
                `/projects/types/899704fd-10be-4aac-8b36-cafccc3f0f0a/questions`
            )
            return res.data
        } catch (error) {
            console.error('Erro ao obter perguntas do projeto:', error)
            throw error
        }
    }
    async retryDocumentProcessing(
        project_id: string,
        document_id: string
    ): Promise<void> {
        try {
            await this.documentApi.put(`/projects/${project_id}/retry`, {
                documents: [document_id],
            })
        } catch (error) {
            console.error('Erro ao reenviar processamento do documento:', error)
            throw error
        }
    }

    async downloadDocument(
        project_id: string,
        document_id: string,
        document_name: string
    ): Promise<void> {
        try {
            const response = await this.documentApi.get(
                `/projects/${project_id}/${document_id}/download`,
                {responseType: 'blob'}
            )

            const url = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', `${document_name}.docx`)
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
        } catch (error) {
            console.error('Erro ao baixar documento:', error)
            throw error
        }
    }

    async cloneProject(project_id: string, body: any): Promise<project> {
        try {
            const res = await this.documentApi.post(
                `/projects/clone/${project_id}`,
                body
            )
            return res.data
        } catch (error) {
            console.error('Erro ao clonar projeto:', error)
            throw error
        }
    }

    //Rota nova do back 
    async postQuestionPreSignedUrl(data: {
        filename: string
        question_uuid: string
        project_uuid: string
    }): Promise<{ url: string; tags: string }> {
        try {
            const res = await this.documentApi.post('/questions/pre-signed-url', data)
            return res.data
        } catch (error) {
            console.error('Erro ao obter URL pré-assinada para upload:', error)
            throw error
        }
    }
}
